
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { User, LogOut, Shield, Settings, CreditCard, Sparkles, ExternalLink, Loader2, Users, Tag, FolderOpen, HelpCircle, BookOpen } from 'lucide-react';
import { useEntitlements } from '@/contexts/EntitlementContext';
import { usePortalSession } from '@/hooks/usePortalSession';

const UserMenu = () => {
  const { user, signOut, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { memberships } = useEntitlements();
  const { open: openPortal, loading: portalLoading } = usePortalSession();
  const hasPaidPlan = memberships.some((p) => p.key && p.key !== 'free');

  if (!user) {
    return (
      <Button onClick={() => navigate('/auth')} variant="ghost" className="text-white hover:bg-white/10">
        Sign In
      </Button>
    );
  }

  const handleSignOut = async () => {
    await signOut();
    localStorage.removeItem('storage_consent');
    navigate('/auth', { replace: true });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          aria-label="My account menu"
          title="My account"
          className="text-white hover:bg-white/10 flex items-center gap-2 px-2 xl:px-4"
        >
          <User className="h-4 w-4" />
          <span className="hidden xl:inline">My Account</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={() => navigate('/projects')}>
          <FolderOpen className="mr-2 h-4 w-4" />
          Projects
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/account')}>
          <Settings className="mr-2 h-4 w-4" />
          My Account
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/billing')}>
          <CreditCard className="mr-2 h-4 w-4" />
          Billing
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/team')}>
          <Users className="mr-2 h-4 w-4" />
          Team
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/pricing')}>
          <Tag className="mr-2 h-4 w-4" />
          Pricing & Plans
        </DropdownMenuItem>
        {hasPaidPlan && (
          <DropdownMenuItem
            onClick={(e) => {
              e.preventDefault();
              if (!portalLoading) openPortal();
            }}
            disabled={portalLoading}
          >
            {portalLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ExternalLink className="mr-2 h-4 w-4" />
            )}
            Manage Subscription
          </DropdownMenuItem>
        )}
        {isAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/admin')}>
              <Shield className="mr-2 h-4 w-4" />
              Admin Panel
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => window.dispatchEvent(new Event('ledlogic:show-tour'))}>
          <HelpCircle className="mr-2 h-4 w-4" />
          Guided tour
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/help')}>
          <BookOpen className="mr-2 h-4 w-4" />
          Help & glossary
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut}>
          <LogOut className="mr-2 h-4 w-4" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
